import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { RootState } from "~/redux/store";
import { fetchRelatedProductsThunk } from "~/redux/products/relatedProducts/actions";
import uniq from "lodash/uniq";
import type { ProductData } from "~/models/ProductData";
import { userIsCustomer } from "~/utils/roles";

export function useMultipleRelatedProducts(productCodes: string[]): {
  state: "loaded" | "loading";
  relatedProducts: ProductData[];
} {
  const dispatch = useDispatch();
  const codes = useSelector(selectMultipleRelatedProductCodes(productCodes), shallowEqual);
  const isLoaded = !userIsCustomer() || codes.every((item) => item?.state === "loaded") ? "loaded" : "loading";
  const uniqueCodes = uniq(codes.map((code) => code?.data).flat());
  const relatedProducts = useSelector(selectRelatedProducts(uniqueCodes as string[]), shallowEqual);

  useEffect(() => {
    productCodes.forEach((productCode) => {
      dispatch(fetchRelatedProductsThunk(productCode));
    });
  }, []);

  return useMemo(() => {
    return { state: isLoaded, relatedProducts: relatedProducts };
  }, [codes, relatedProducts]);
}

export function useRelatedProducts(productCode: string) {
  const dispatch = useDispatch();
  const relatedProductCodes = useSelector(selectRelatedProductCodes(productCode));
  const relatedProducts = useSelector(selectRelatedProducts(relatedProductCodes?.data ?? []), shallowEqual);

  useEffect(() => {
    if (!relatedProductCodes) {
      dispatch(fetchRelatedProductsThunk(productCode));
    }
  }, [productCode, relatedProductCodes]);

  return useMemo(() => {
    return { state: relatedProductCodes?.state ?? "loading", relatedProducts: relatedProducts ?? [] };
  }, [relatedProductCodes, relatedProducts]);
}

const selectRelatedProductCodes = (productCode: string) => (state: RootState) =>
  state.products.relatedProducts[productCode];

const selectMultipleRelatedProductCodes = (productCodes: string[]) => (state: RootState) => {
  return productCodes.map((productCode) => state.products.relatedProducts[productCode]);
};

const selectRelatedProducts = (productCodes: string[]) => (state: RootState) =>
  productCodes.map((c) => state.products.basic[c]).filter(Boolean);
